import { useEffect, useState } from 'react';

/**
 * Does the viewport match a CSS media query, live. A per-viewer layout convenience only: where `matchMedia` does not
 * exist (jsdom, an old browser) the answer is `false` and nothing breaks - the layout simply stays as the agent chose it.
 */
export function useMediaQuery(query: string): boolean {
  const read = (): boolean => {
    try {
      return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
    } catch {
      return false;
    }
  };
  const [matches, setMatches] = useState<boolean>(read);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia(query);
    } catch {
      return undefined;
    }
    const onChange = () => setMatches(mql.matches);
    onChange();
    // Safari < 14 only has the deprecated addListener/removeListener pair.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}
